import { Localizable } from '@components/localizable/Localizable'
import {
  baseFormItemName,
  isSwitchEnabledFromValues,
  switchFormItemName,
} from '@models/business/Policy'
import {
  getBillingMethods,
  selectBillingMethods,
} from '@store/business/BillingMethodReducer'
import { getDays, selectDays } from '@store/business/DayReducer'
import { selectSelectedPolicy } from '@store/business/PolicyReducer'
import { getShifts, selectShifts } from '@store/business/ShiftReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { SBRMFormActionProps } from '@supplement-bacon/sbrm-react'
import { requiredRule } from '@utils/rules'
import { capitalizeFirstLetter } from '@utils/string'
import {
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd'
import { Fragment, useEffect } from 'react'

const { Title, Text } = Typography

const Update = ({ form, formProps }: SBRMFormActionProps) => {
  const dispatch = useAppDispatch()
  const days = useAppSelector(selectDays())
  const shifts = useAppSelector(selectShifts())
  const item = useAppSelector(selectSelectedPolicy())
  const billingMethods = useAppSelector(selectBillingMethods())

  useEffect(() => {
    if (!item) {
      return
    }
    Promise.all([
      dispatch(getDays(initialQuery)),
      dispatch(getShifts(initialQuery)),
      dispatch(getBillingMethods(initialQuery)),
    ]).then(() => {
      form.setFieldsValue({
        ...item,
        policyShifts: item.policyShifts.reduce(
          (acc, e) => ({
            ...acc,
            [e.day + '-' + e.shift]: {
              enabled: true,
              billingMethod: e.billingMethod,
              day: e.day,
              shift: e.shift,
            },
          }),
          {}
        ),
      })
    })
  }, [item])

  if (!item) return <></>

  return (
    <Form {...formProps} form={form} layout="vertical">
      <Form.Item name="id" hidden style={{ display: 'none' }}>
        <Input type="hidden" />
      </Form.Item>

      <Form.Item name="name" rules={[requiredRule()]} label="Name">
        <Input />
      </Form.Item>

      <Localizable.TextArea
        rules={[requiredRule()]}
        name="description"
        label="Public description"
      />

      <Space orientation="vertical" size="small" className="w-full">
        {days.map((day) => (
          <Fragment key={'day-' + day.id}>
            <Row gutter={[16, 16]} align="middle">
              <Col span={8}>
                <Title level={5} style={{ margin: 0 }}>
                  {day.name}
                </Title>
              </Col>
              {shifts.map((shift) => (
                <Col span={8} key={'day-' + day.id + 'shift-' + shift.id}>
                  <Form.Item
                    noStyle
                    valuePropName="checked"
                    name={switchFormItemName(day.id, shift.id)}
                  >
                    <Switch
                      unCheckedChildren={capitalizeFirstLetter(shift.name)}
                      checkedChildren={capitalizeFirstLetter(shift.name)}
                    />
                  </Form.Item>
                  <Form.Item
                    hidden
                    noStyle
                    initialValue={day.id}
                    style={{ display: 'none' }}
                    name={[...baseFormItemName(day.id, shift.id), 'day']}
                  >
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item
                    hidden
                    noStyle
                    initialValue={shift.id}
                    style={{ display: 'none' }}
                    name={[...baseFormItemName(day.id, shift.id), 'shift']}
                  >
                    <Input type="hidden" />
                  </Form.Item>
                </Col>
              ))}
            </Row>

            {shifts.map((shift, shiftIndex) => (
              <Row key={'day-' + day.id + '-shift-' + shiftIndex}>
                <Col span={24}>
                  <Form.Item
                    noStyle
                    key={`${day.id}-${shift.id}-details`}
                    shouldUpdate={(prev, curr) =>
                      isSwitchEnabledFromValues(prev, day.id, shift.id) !==
                      isSwitchEnabledFromValues(curr, day.id, shift.id)
                    }
                  >
                    {({ getFieldValue }) => {
                      const enabled = getFieldValue(
                        switchFormItemName(day.id, shift.id)
                      )
                      if (!enabled) return <></>

                      return (
                        <Card
                          size="small"
                          style={{ marginTop: 4 }}
                          key={shift.id}
                        >
                          <Row gutter={[16, 16]} align="middle">
                            <Col span={4}>
                              <Text type="secondary">
                                {capitalizeFirstLetter(shift.name)}
                              </Text>
                            </Col>
                            <Col span={20}>
                              <Form.Item
                                noStyle
                                rules={[requiredRule()]}
                                name={[
                                  ...baseFormItemName(day.id, shift.id),
                                  'billingMethod',
                                ]}
                              >
                                <Select
                                  placeholder="Billing Method"
                                  style={{ width: '100%' }}
                                  options={billingMethods.map((method) => ({
                                    label: method.name,
                                    value: method.id,
                                  }))}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      )
                    }}
                  </Form.Item>
                </Col>
              </Row>
            ))}
          </Fragment>
        ))}
      </Space>
    </Form>
  )
}

export default Update
